import {AvailabilityManifestSchema} from '@repo/shared/api/catalogue_availability';
import {Arr, Num, Obj, Opt, OptBool, OptNum, OptStr, Str} from '@repo/shared/api/core/api_schema';
import type {SchemaToType} from '@repo/shared/api/core/api_schema';

export const CATALOGUE_VERSION = 1;
export const CATALOGUE_PREFIX = 'catalogue/v1/';
export const MAX_CATALOGUE_PARTITION_BYTES = 32 * 1024 * 1024;
export const MAX_CATALOGUE_PARTITION_MOVIES = 60_000;
export const MAX_CATALOGUE_PARTITIONS = 300;
export const MAX_CATALOGUE_MOVIES = 300_000;

export const CatalogueMovieSchema = Obj({
  imdbId: Str(),
  title: Str(),
  posterPath: OptStr(),
  releaseDate: OptNum(),
  genreIds: Arr(Num()),
  voteAverage: OptNum(),
  voteCount: OptNum(),
  popularity: OptNum(),
  budget: OptNum(),
  revenue: OptNum(),
  runtime: OptNum(),
  firstPubTs: Num(),
});
export type CatalogueMovie = SchemaToType<typeof CatalogueMovieSchema>;
export const CataloguePartitionSchema = Obj({
  id: Str(),
  hash: Str(),
  bytes: Num(),
  compressedBytes: Num(),
  count: Num(),
});
export type CataloguePartition = SchemaToType<typeof CataloguePartitionSchema>;
export interface CatalogueManifest {
  schemaVersion: number;
  partitions: CataloguePartition[];
}
export interface CatalogueShard {
  schemaVersion: number;
  id: string;
  movies: CatalogueMovie[];
}
export const CATALOGUE_SYNC_API = {
  '/catalogue/sync': {
    POST: {
      req: Obj({
        partitions: Arr(Obj({id: Str(), hash: Str()})),
        includeAvailability: OptBool(),
        availabilityHash: OptStr(),
      }),
      res: Obj({
        schemaVersion: Num(),
        partitions: Arr(CataloguePartitionSchema),
        downloads: Arr(Obj({id: Str(), hash: Str(), url: Str()})),
        expiresAt: Num(),
        availability: Opt(Obj({...AvailabilityManifestSchema.properties, url: OptStr()})),
      }),
    },
  },
  '/catalogue/movie': {
    POST: {req: Obj({imdbId: Str()}), res: Obj({overview: Str()})},
  },
};

export function isCataloguePartition(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    (value === 'unknown' ||
      value === 'before-1970' ||
      /^(1970-1979|1980-1989|1990-1999|2000-2009|20[1-9]\d|21\d{2})$/u.test(value))
  );
}
export function compareCataloguePartitions(a: string, b: string): number {
  // Download recent films first; leave the undated partition until last.
  const start = (id: string) =>
    id === 'unknown' ? 0 : id === 'before-1970' ? 1800 : Number(id.slice(0, 4));
  return start(b) - start(a);
}
export function isCatalogueHash(value: unknown): value is string {
  return typeof value === 'string' && /^[a-f0-9]{64}$/u.test(value);
}
function record(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}
export function isCatalogueMovie(value: unknown): value is CatalogueMovie {
  if (!record(value)) return false;
  const numbers = [
    'releaseDate',
    'voteAverage',
    'voteCount',
    'popularity',
    'budget',
    'revenue',
    'runtime',
  ];
  return (
    typeof value['imdbId'] === 'string' &&
    /^\d{7,10}$/u.test(value['imdbId']) &&
    typeof value['title'] === 'string' &&
    value['title'].length > 0 &&
    value['title'].length <= 240 &&
    (value['posterPath'] === undefined ||
      (typeof value['posterPath'] === 'string' &&
        /^\/[a-zA-Z0-9_.-]+\.(jpg|png)$/u.test(value['posterPath']))) &&
    Array.isArray(value['genreIds']) &&
    value['genreIds'].length <= 30 &&
    value['genreIds'].every(id => Number.isSafeInteger(id) && id > 0) &&
    typeof value['firstPubTs'] === 'number' &&
    Number.isFinite(value['firstPubTs']) &&
    numbers.every(
      key =>
        value[key] === undefined || (typeof value[key] === 'number' && Number.isFinite(value[key])),
    )
  );
}
export function movieCataloguePartition(movie: CatalogueMovie): string {
  if (movie.releaseDate === undefined) return 'unknown';
  const year = new Date(movie.releaseDate).getUTCFullYear();
  if (!Number.isInteger(year) || year < 1800 || year > 2199) return 'unknown';
  if (year < 1970) return 'before-1970';
  if (year < 2010) {
    const decade = Math.floor(year / 10) * 10;
    return `${decade}-${decade + 9}`;
  }
  return String(year);
}
export function isCatalogueManifest(value: unknown): value is CatalogueManifest {
  if (
    !record(value) ||
    value['schemaVersion'] !== CATALOGUE_VERSION ||
    !Array.isArray(value['partitions']) ||
    value['partitions'].length > MAX_CATALOGUE_PARTITIONS
  )
    return false;
  let total = 0;
  const seen = new Set<string>();
  for (const item of value['partitions']) {
    if (
      !record(item) ||
      !isCataloguePartition(item['id']) ||
      !isCatalogueHash(item['hash']) ||
      seen.has(item['id'])
    )
      return false;
    if (
      !['bytes', 'compressedBytes', 'count'].every(
        key => Number.isSafeInteger(item[key]) && Number(item[key]) > 0,
      )
    )
      return false;
    if (
      Number(item['bytes']) > MAX_CATALOGUE_PARTITION_BYTES ||
      Number(item['compressedBytes']) > MAX_CATALOGUE_PARTITION_BYTES ||
      Number(item['count']) > MAX_CATALOGUE_PARTITION_MOVIES
    )
      return false;
    total += Number(item['count']);
    seen.add(item['id']);
  }
  return total <= MAX_CATALOGUE_MOVIES;
}
export function isCatalogueShard(
  value: unknown,
  expected: CataloguePartition,
): value is CatalogueShard {
  if (
    !record(value) ||
    value['schemaVersion'] !== CATALOGUE_VERSION ||
    value['id'] !== expected.id ||
    !Array.isArray(value['movies']) ||
    value['movies'].length !== expected.count
  )
    return false;
  const seen = new Set<string>();
  for (const movie of value['movies']) {
    if (
      !isCatalogueMovie(movie) ||
      movieCataloguePartition(movie) !== expected.id ||
      seen.has(movie.imdbId)
    )
      return false;
    seen.add(movie.imdbId);
  }
  return true;
}
export function catalogueObjectKey(partition: CataloguePartition): string {
  return `${CATALOGUE_PREFIX}${partition.id}.${partition.hash}.json`;
}
